/**
 * Dubai Fitness Challenge brand tokens for the DFC school experience surfaces.
 *
 * Values follow the DFC Brand Guidelines (June 2026), Part 3: Design Elements.
 *
 * Primary palette — the only brand-expressive colours:
 *   blue  #00BEFF  (Pantone 306C)
 *   green #54F20A  (Pantone 7488C)
 *   black #000000
 *   white #FFFFFF
 *
 * blueDark (#2D6C8E) and greenDark (#3CAD07) exist ONLY as end stops of the
 * sanctioned gradients. They are not standalone palette colours.
 *
 * Exactly three gradients are permitted, and they are always linear:
 *   blue -> green, green -> greenDark, blue -> blueDark.
 *
 * DFC_NEUTRAL holds functional UI colours (hairlines, muted text, error state)
 * that the brand palette does not define. They sit outside the palette on
 * purpose and must not be used as brand-expressive colour.
 *
 * Contrast: white on blue is 2.14:1 and white on green is 1.49:1 — both fail
 * WCAG AA. Text placed on blue or green must be black (9.8:1 and 14.1:1).
 */

/** The DFC hex values. Style with `DFC_PALETTE`; read these only where a raw hex is required. */
export const DFC_PALETTE_HEX = {
  blue: '#00BEFF',
  green: '#54F20A',
  black: '#000000',
  white: '#FFFFFF',
  /** Gradient end stop only — never use standalone */
  blueDark: '#2D6C8E',
  /** Gradient end stop only — never use standalone */
  greenDark: '#3CAD07',
} as const;

export type DfcPaletteKey = keyof typeof DFC_PALETTE_HEX;

/** A brand palette filling DFC's slots by role: blue = primary accent, green = secondary, black = ink. */
export type BrandPalette = Record<DfcPaletteKey, string>;

/** The CSS custom property behind each slot. `asaBrand.ts` sets them; nothing does for DFC. */
export const PALETTE_CSS_VAR: Record<DfcPaletteKey, string> = {
  blue: '--village-blue',
  green: '--village-green',
  black: '--village-black',
  white: '--village-white',
  blueDark: '--village-blue-dark',
  greenDark: '--village-green-dark',
};

const hexToRgb = (hex: string): [number, number, number] => {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : normalized;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
};

/** "0, 190, 255" — the value of a slot's `-rgb` companion property. */
export const hexToRgbChannels = (hex: string): string => hexToRgb(hex).join(', ');

const paletteVar = (key: DfcPaletteKey) => `var(${PALETTE_CSS_VAR[key]}, ${DFC_PALETTE_HEX[key]})`;

/**
 * The palette every DFC village style reads. Each value is a CSS custom property falling back to
 * the DFC hex (`var(--village-blue, #00BEFF)`). Nothing sets the properties for DFC, so DFC
 * renders exactly the hex above; for All Sports Arena `asaBrand.ts` sets them on `:root` and the
 * same styles repaint.
 */
export const DFC_PALETTE = {
  blue: paletteVar('blue'),
  green: paletteVar('green'),
  black: paletteVar('black'),
  white: paletteVar('white'),
  /** Gradient end stop only — never use standalone */
  blueDark: paletteVar('blueDark'),
  /** Gradient end stop only — never use standalone */
  greenDark: paletteVar('greenDark'),
} as const;

const PALETTE_VAR_PATTERN = /^var\((--[\w-]+),\s*(#[0-9a-fA-F]{3}|#[0-9a-fA-F]{6})\)$/;

/**
 * Builds an rgba() string from a brand colour so colour literals never get hand-written. Takes a
 * hex, or a `DFC_PALETTE` slot, whose result keeps following the slot at runtime:
 * `rgba(var(--village-blue-rgb, 0, 190, 255), 0.4)`.
 */
export const withAlpha = (color: string, alpha: number): string => {
  const slot = PALETTE_VAR_PATTERN.exec(color);
  if (slot) return `rgba(var(${slot[1]}-rgb, ${hexToRgbChannels(slot[2])}), ${alpha})`;
  return `rgba(${hexToRgbChannels(color)}, ${alpha})`;
};

/**
 * Ink on a `DFC_PALETTE.green` fill (the confirmation tick, the profile avatar). DFC: black. A
 * brand whose green slot is too dark for that ink sets `--village-on-accent` (`asaBrand.ts`).
 */
export const DFC_ON_ACCENT = `var(--village-on-accent, ${DFC_PALETTE.black})`;

/** The three sanctioned gradients. No other colour pairing may be used as a gradient. */
export const DFC_GRADIENT = {
  blueToGreen: (angle = '135deg') =>
    `linear-gradient(${angle}, ${DFC_PALETTE.blue} 0%, ${DFC_PALETTE.green} 100%)`,
  greenToGreenDark: (angle = '135deg') =>
    `linear-gradient(${angle}, ${DFC_PALETTE.green} 0%, ${DFC_PALETTE.greenDark} 100%)`,
  blueToBlueDark: (angle = '135deg') =>
    `linear-gradient(${angle}, ${DFC_PALETTE.blue} 0%, ${DFC_PALETTE.blueDark} 100%)`,
} as const;

/** Functional UI colours outside the brand palette. Not brand-expressive. */
export const DFC_NEUTRAL = {
  border: '#e5e7eb',
  muted: '#6b7280',
  /** A CSS property falling back to DFC's red, so a brand on a dark surface can lift it (`asaBrand.ts`). */
  error: 'var(--village-error, #c82e34)',
  panel: 'rgba(255, 255, 255, 0.92)',
  overlay: 'rgba(0, 0, 0, 0.35)',
} as const;

export const DFC_BRAND = {
  // Palette
  blue: DFC_PALETTE.blue,
  green: DFC_PALETTE.green,
  black: DFC_PALETTE.black,
  white: DFC_PALETTE.white,

  // Type
  text: DFC_PALETTE.black,
  textSoft: withAlpha(DFC_PALETTE.black, 0.82),
  muted: DFC_NEUTRAL.muted,
  error: DFC_NEUTRAL.error,

  // Surfaces
  border: DFC_NEUTRAL.border,
  panel: DFC_NEUTRAL.panel,
  overlay: DFC_NEUTRAL.overlay,

  /** Signature brand gradient (blue -> green). Always pair with black text. */
  heroGradient: DFC_GRADIENT.blueToGreen(),
  /** Subtle page wash: a tint of the brand blue fading to white. */
  pageWash: `linear-gradient(165deg, ${withAlpha(DFC_PALETTE.blue, 0.1)} 0%, ${withAlpha(
    DFC_PALETTE.blue,
    0.03
  )} 42%, ${DFC_PALETTE.white} 100%)`,

  // Effects
  blueGlow: `0 8px 25px ${withAlpha(DFC_PALETTE.blue, 0.4)}`,
  greenShine: withAlpha(DFC_PALETTE.green, 0.35),
  shadowSoft: `0 4px 18px ${withAlpha(DFC_PALETTE.black, 0.06)}`,
  shadowCard: `0 6px 20px ${withAlpha(DFC_PALETTE.black, 0.08)}`,
  shadowPanel: `0 6px 24px ${withAlpha(DFC_PALETTE.blue, 0.08)}`,

  maxWidth: 832,
} as const;
